import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { List, ListItem } from '@design-system/List';
import { Skeleton } from '@design-system/Skeleton';

import { useOrderDetailViewModel } from '@/features/OrderTrackingViewModels';
import {
  ORDER_STATE_PROGRESS,
  ORDER_STATE_TEXT,
} from '@/components/screens/SMP/components';
import { analyticsEvent } from '@/utils/analyticsEvent';
import { EventTracker } from '@/utils';
import type {
  AccountLacoreOrder,
  AccountOrder,
  AccountOrderLineItem,
} from '@viome/viome-base/src/usecase/AccountOrders/AccountOrders.model';
import type { AddressInfo } from '@viome/viome-base/src/usecase/OrderTracking/OrderTracking.model';
import { NavBar } from '@design-system/NavBar.tsx';

type AccountOrderLocationState = {
  accountOrder?: AccountOrder;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return `$${amount.toFixed(2)}`;
};

const getLacoreStatusText = (lacoreOrder: AccountLacoreOrder) => {
  if (!lacoreOrder.current_state) {
    return lacoreOrder.status_text || 'Order Status';
  }
  return (
    ORDER_STATE_TEXT[lacoreOrder.current_state] ??
    lacoreOrder.status_text ??
    lacoreOrder.current_state
  );
};

const formatFulfillmentStatus = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

const ProductRow = ({ lineItem }: { lineItem?: AccountOrderLineItem }) => {
  if (!lineItem) return null;
  return (
    <div className="flex items-center gap-5">
      {lineItem.image ? (
        <img
          src={lineItem.image}
          alt={lineItem.title || 'Product'}
          className="size-[72px] rounded-[12px] object-cover shrink-0"
        />
      ) : (
        <div
          className="size-[72px] rounded-[12px] bg-background-secondary shrink-0"
          aria-hidden
        />
      )}
      <span className="typography-body text-primary">
        {lineItem.title || 'Order'}
      </span>
    </div>
  );
};

const LacoreTrackingCard = ({
  lacoreOrder,
  lineItem,
}: {
  lacoreOrder: AccountLacoreOrder;
  lineItem?: AccountOrderLineItem;
}) => {
  const isDelivered = lacoreOrder.current_state === 'Delivered';
  const progressPercent = lacoreOrder.current_state
    ? (ORDER_STATE_PROGRESS[lacoreOrder.current_state] ?? 0)
    : 0;

  if (isDelivered) {
    return (
      <Card className="pb-6" data-testid="orderDetail-deliveredCard">
        <CardHeader>
          <CardTitle className="typography-h2 text-primary">
            Order Delivered
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <span className="typography-h3 text-primary">
            Your order was delivered
            {lacoreOrder.delivery_date
              ? ` on ${formatDate(lacoreOrder.delivery_date)}`
              : ''}
            .
          </span>
          <ProductRow lineItem={lineItem} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="pb-6" data-testid="orderDetail-trackingCard">
      <CardHeader>
        <CardTitle className="typography-h2 text-primary">
          Order in Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <span className="typography-h3 text-primary">
          {getLacoreStatusText(lacoreOrder)}
        </span>

        <div className="flex flex-col gap-3">
          <div className="relative w-full py-2">
            <div className="w-full h-1 rounded-full bg-stroke-secondary-subtle" />
            <div
              className="absolute top-1/2 -translate-y-1/2 left-0 h-1 rounded-full bg-success transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {!!lacoreOrder.available_states?.length && (
            <div className="flex justify-between">
              {lacoreOrder.available_states.map((state) => (
                <span
                  key={state}
                  className="typography-caption2 text-secondary"
                >
                  {state}
                </span>
              ))}
            </div>
          )}
        </div>

        <ProductRow lineItem={lineItem} />
      </CardContent>
    </Card>
  );
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="typography-h2 text-primary px-1">{children}</h2>
);

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <ListItem hideIndicator>
    <span className="typography-body text-primary flex-1">{label}</span>
    <span className="typography-caption1 text-tertiary">{value}</span>
  </ListItem>
);

const AddressBlock = ({
  address,
  testIdPrefix,
}: {
  address: AddressInfo | null;
  testIdPrefix: string;
}) => {
  if (!address) return <p className="text-tertiary">—</p>;

  return (
    <div
      className="flex flex-col typography-body text-tertiary fs-mask"
      data-testid={`${testIdPrefix}-address`}
    >
      <span>{address.full_name}</span>
      <span>{address.address_line1}</span>
      {address.address_line2 && <span>{address.address_line2}</span>}
      <span>
        {address.city}, {address.state} {address.zip}
      </span>
      {address.country && <span>{address.country}</span>}
    </div>
  );
};

const mapAccountOrderAddress = (
  address?: AccountOrder['shipping_address'],
): AddressInfo | null => {
  if (!address) return null;
  return {
    full_name: address.full_name ?? null,
    address_line1: address.address_line1 ?? null,
    address_line2: address.address_line2 ?? null,
    city: address.city ?? null,
    state: address.state ?? null,
    zip: address.zip ?? null,
    country: address.country ?? null,
  };
};

const mapAccountOrderToDetails = (order: AccountOrder) => ({
  shipping_information: mapAccountOrderAddress(order.shipping_address),
  order_information: {
    order_date: order.order_date ?? null,
    order_number: order.shopify_order_number ?? '',
    order_total: order.total_price ?? null,
    fulfillment_status: order.fulfillment_status ?? null,
  },
  payment_information: mapAccountOrderAddress(order.billing_address),
  transaction_details: {
    discount_code: null,
    overall_price: order.subtotal_price ?? null,
    total_tax: order.total_tax ?? null,
    total_discounts: order.total_discounts ?? null,
    final_price: order.total_price ?? null,
  },
  line_item: order.line_items?.[0],
  lacore_order: order.lacore_order ?? null,
});

export const OrderDetailPage = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const location = useLocation();
  const accountOrderFromState = (
    location.state as AccountOrderLocationState | null
  )?.accountOrder;

  const hasAccountOrder =
    !!accountOrderFromState &&
    accountOrderFromState.shopify_order_number === orderNumber;

  const { orderDetails, isOrderDetailsLoading, isOrderDetailsError } =
    useOrderDetailViewModel(hasAccountOrder ? '' : (orderNumber ?? ''));

  const details = useMemo(() => {
    if (hasAccountOrder && accountOrderFromState) {
      return mapAccountOrderToDetails(accountOrderFromState);
    }
    if (!orderDetails) return null;
    return {
      ...orderDetails,
      order_information: orderDetails.order_information
        ? {
            ...orderDetails.order_information,
            fulfillment_status: null as string | null,
          }
        : null,
      line_item: undefined,
      lacore_order: null,
    };
  }, [hasAccountOrder, accountOrderFromState, orderDetails]);

  useEffect(() => {
    EventTracker.trackEvent(
      'account',
      analyticsEvent.ORDER_TRACKING_ORDER_DETAIL_VIEW,
    );
  }, []);

  if (!hasAccountOrder && isOrderDetailsLoading) {
    return (
      <section
        className="page-container flex flex-col gap-10 pb-24"
        data-testid="orderDetail-skeleton"
      >
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if ((!hasAccountOrder && isOrderDetailsError) || !details) {
    return (
      <section
        className="page-container flex flex-col gap-12 pb-24"
        data-testid="orderDetail-empty"
      >
        <div className="flex flex-col gap-6">
          <NavBar returnButtonTitle="Back" className="md:-ml-9" />
          <h1
            className="typography-h6 text-primary"
            data-testid="orderDetail-title"
          >
            Order Details
          </h1>
        </div>
        <div className="flex flex-col gap-6">
          <SectionTitle>Order Information</SectionTitle>
          <Card>
            <CardContent className="py-5 px-5">
              <span
                className="typography-body text-secondary"
                data-testid="orderDetail-emptyMessage"
              >
                No order details available at this time.
              </span>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  const {
    shipping_information,
    order_information,
    payment_information,
    transaction_details,
    line_item,
    lacore_order,
  } = details;

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="orderDetail-page"
    >
      <div className="flex flex-col gap-6" data-testid="orderDetail-header">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <h1
          className="typography-h6 text-primary"
          data-testid="orderDetail-title"
        >
          Order Details
        </h1>

        {lacore_order ? (
          <LacoreTrackingCard
            lacoreOrder={lacore_order}
            lineItem={line_item}
          />
        ) : (
          line_item && (
            <Card data-testid="orderDetail-productCard">
              <CardContent className="flex flex-col gap-4 p-6">
                <ProductRow lineItem={line_item} />
              </CardContent>
            </Card>
          )
        )}
      </div>

      {shipping_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Shipping Information</SectionTitle>
          <Card data-testid="orderDetail-shippingCard">
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span
                  className="typography-body text-primary"
                  data-testid="orderDetail-shippingTitle"
                >
                  Shipping Address
                </span>
                <AddressBlock
                  address={shipping_information}
                  testIdPrefix="orderDetail-shipping"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {order_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Order Information</SectionTitle>
          <Card data-testid="orderDetail-orderInfoCard">
            <CardContent className="p-0">
              <List data-testid="orderDetail-orderInfoList">
                <InfoRow
                  label="Date"
                  value={formatDate(order_information.order_date)}
                />
                <InfoRow
                  label="Order Number"
                  value={`#${order_information.order_number}`}
                />
                {order_information.fulfillment_status && (
                  <InfoRow
                    label="Status"
                    value={formatFulfillmentStatus(
                      order_information.fulfillment_status,
                    )}
                  />
                )}
                <InfoRow
                  label="Total"
                  value={formatCurrency(order_information?.order_total)}
                />
              </List>
            </CardContent>
          </Card>
        </div>
      )}

      {payment_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Payment Information</SectionTitle>
          <Card data-testid="orderDetail-paymentCard">
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span
                  className="typography-body text-primary"
                  data-testid="orderDetail-paymentTitle"
                >
                  Billing Address
                </span>
                <AddressBlock
                  address={payment_information}
                  testIdPrefix="orderDetail-payment"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {transaction_details && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Transaction Details</SectionTitle>
          <Card data-testid="orderDetail-transactionCard">
            <CardContent className="p-0">
              <List data-testid="orderDetail-transactionList">
                <InfoRow
                  label="Price"
                  value={formatCurrency(transaction_details?.overall_price)}
                />
                <InfoRow
                  label="Tax"
                  value={formatCurrency(transaction_details?.total_tax)}
                />
                {transaction_details?.total_discounts != null &&
                  transaction_details.total_discounts > 0 && (
                    <InfoRow
                      label="Discount"
                      value={`-${formatCurrency(transaction_details.total_discounts)}`}
                    />
                  )}
                <InfoRow
                  label="Order Total"
                  value={formatCurrency(transaction_details?.final_price)}
                />
              </List>
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
};
